import { useState, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { Search, Boxes } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Common icons organized by category
const COMMON_ICONS = [
  // Items/Objects
  'Boxes',
  'Package',
  'ShoppingBag',
  'Book',
  'GraduationCap',
  'BookOpen',
  'FileText',
  'Folder',
  // Locations
  'Building2',
  'MapPin',
  'Home',
  'School',
  'Warehouse',
  'Store',
  // People
  'Users',
  'User',
  'UserCheck',
  'UserPlus',
  // Activity
  'Activity',
  'Signal',
  'Rss',
  'Radio',
  'Wifi',
  // Agriculture
  'TreePine',
  'Sprout',
  'Wheat',
  'Leaf',
  // Analytics
  'BarChart3',
  'LineChart',
  'PieChart',
  'TrendingUp',
  // Other common
  'Settings',
  'Cog',
  'Grid',
  'List',
  'Search',
  'Filter',
] as const;

interface IconSelectorProps {
  value?: string;
  onValueChange: (iconName: string) => void;
  disabled?: boolean;
}

export function IconSelector({
  value,
  onValueChange,
  disabled,
}: IconSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Get icon component from string name
  const getIconComponent = (iconName: string): React.ComponentType<{ className?: string }> => {
    const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName];
    return IconComponent || Boxes;
  };

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    const searchLower = search.toLowerCase();
    if (!searchLower) {
      return COMMON_ICONS;
    }
    return COMMON_ICONS.filter((iconName) =>
      iconName.toLowerCase().includes(searchLower),
    );
  }, [search]);

  const SelectedIcon = value ? getIconComponent(value) : Boxes;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start"
          disabled={disabled}
        >
          <SelectedIcon className="mr-2 h-4 w-4" />
          <span className="flex-1 text-left">
            {value || 'Select icon'}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[400px]">
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <div className="grid grid-cols-4 gap-2">
              {filteredIcons.map((iconName) => {
                const IconComponent = getIconComponent(iconName);
                const isSelected = value === iconName;
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => {
                      onValueChange(iconName);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'flex h-12 w-full items-center justify-center rounded-md border transition-colors hover:bg-accent',
                      isSelected && 'border-primary bg-accent',
                    )}
                    title={iconName}
                  >
                    <IconComponent className="h-5 w-5" />
                  </button>
                );
              })}
            </div>
            {filteredIcons.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No icons found
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
